import type { ShorthandValue, TileImageProps } from '@archipro-design/aria';

import type { DetailedPinWithChecked } from '../component/all-pins/AllPins';
import { TileTopAction } from '../component/shared/TileTopAction';
import { DesignBoardPinTargetType } from 'generated/graphql';

import type { ArchizeenTileData } from '~/modules/design-board/type';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { cn } from '@rocco/utils/cn';

import { Checkbox, pxToRem, useTheme } from '@archipro-design/aria';
import { MoodboardItemActionBar } from '../../build-hub/components/moodboard/item/action-bar';
import { useMoodboardItemDetailDrawer } from '../../build-hub/components/moodboard/item/item-detail-drawer';
export const PinTileContent = ({
    pin,
    isChecked,
    onUpdate,
    onDelete,
    isAllPins,
    canComment,
    canVote,
    buildHubLink,
    canEdit = false,
    hideTileTopAction,
    boardId,
    buildHubId,
}: {
    pin: DetailedPinWithChecked;
    isChecked: boolean;
    onUpdate: (id: number, checked: boolean) => void;
    onDelete: (id: number) => void;
    isAllPins?: boolean;
    canComment?: boolean;
    canVote?: boolean;
    canEdit?: boolean;
    hideTileTopAction?: boolean;
    buildHubLink?: string;
    boardId?: number;
    buildHubId?: number;
}): React.ReactElement => {
    const theme = useTheme();

    const { openDialog } = useMoodboardItemDetailDrawer();

    const deletePin = (id: number) => {
        onDelete(id);
    };

    const isArticleTile = isPinTileType<ArchizeenTileData>(
        pin.TileData,
        DesignBoardPinTargetType.Article
    );

    const isArticle = pin.TargetFilterType === DesignBoardPinTargetType.Article;

    const isBuildHub = !!buildHubId;
    const isMemberUpload =
        pin.Target.__typename === 'MemberUpload' ||
        pin.TargetFilterType === DesignBoardPinTargetType.MemberUpload;

    // For BuildHub pins, use the custom title if available (overrides server-generated TileData.title)
    const tileModel =
        isBuildHub && pin.Title
            ? { ...pin.TileData, title: pin.Title }
            : pin.TileData;

    // Handle click behavior based on pin type
    const handleClick = () => {
        if (isBuildHub) {
            // BuildHub: Open detail drawer
            openDialog(pin);
        } else if (isMemberUpload && pin.Target.__typename === 'MemberUpload') {
            // MemberUpload: Open file in new tab
            const fileUrl = pin.Target.Link;
            if (fileUrl) {
                window.open(fileUrl, '_blank');
            }
        }
    };

    // Determine if the tile link should be disabled
    const shouldDisableLink = isBuildHub || isMemberUpload;

    return (
        <div
            className={cn(
                'group relative',
                isChecked &&
                    '[&_.rocco-tile-media]:border-2 [&_.rocco-tile-media]:border-[#bea78c]'
            )}
        >
            {!hideTileTopAction && (
                <div className="absolute left-0 top-0 z-10 w-full p-2 transition-opacity duration-200 md:p-0 md:opacity-0 md:group-hover:opacity-100 [&>div]:w-full">
                    {isBuildHub ? (
                        <div className="flex justify-end p-4">
                            <Checkbox
                                checked={isChecked}
                                indicator={{
                                    kind: 'checkbox',
                                    indicator: {
                                        variables: {
                                            iconSize: pxToRem(16),
                                            border: `1px solid ${
                                                isChecked
                                                    ? 'transparent'
                                                    : theme.siteVariables.colors
                                                          .grey[100]
                                            }`,
                                            backgroundColor:
                                                'rgb(232, 233, 235)', //no color set for this
                                            size: pxToRem(24),
                                            checkedBackgroundColor:
                                                theme.siteVariables.colors.gold[
                                                    '000'
                                                ],
                                        },
                                    },
                                }}
                                onChange={(e, data) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    onUpdate(pin.ID, data?.checked ?? false);
                                }}
                                key={`checkbox${pin.ID}`}
                            />
                        </div>
                    ) : (
                        <TileTopAction
                            //Implent something for hiding share if type is a file/image
                            isChecked={isChecked}
                            id={pin.ID}
                            onUpdate={onUpdate}
                            deletePin={(id) => deletePin(id)}
                            isAllPins={isAllPins}
                            isArticleTile={isArticleTile}
                            professionalLink={getPinWebsiteLink(pin)}
                            pin={pin}
                            canComment={canComment}
                        />
                    )}
                </div>
            )}
            <Tile
                tile={{
                    aspectRatio: isArticle ? 'article' : 'default',
                    model: {
                        ...tileModel,
                        ...(isArticle && {
                            variant: 'featured',
                        }),
                        badge: null,
                    },
                    hideFollowButton: true,
                    disableLink: shouldDisableLink,
                    onClick:
                        shouldDisableLink ? () => handleClick() : undefined,
                }}
                key={pin.TileData.id}
                inlineActionButtons={
                    isBuildHub && buildHubLink ? (
                        <MoodboardItemActionBar
                            canVote={!!canVote}
                            canComment={!!canComment}
                            canEdit={canEdit}
                            pin={pin}
                            boardLink={buildHubLink}
                            boardId={boardId}
                            onDelete={() => deletePin(pin.ID)}
                        />
                    ) : undefined
                }
            />
        </div>
    );
};

export const isPinTileType = <
    T extends {
        pinType: DesignBoardPinTargetType;
    },
>(
    tile: Record<string, unknown>,
    type: DesignBoardPinTargetType
): tile is T => {
    return 'pinType' in tile && (tile as T).pinType === type;
};

export const getUploadFileTileImage = (
    src: string,
    alt = '',
    objectFit = 'cover'
) => {
    return {
        images: [
            {
                src,
                alt,
                objectFit,
                height: '100%',
                width: '100%',
            },
        ] as TileImageProps['images'],
    } as ShorthandValue<TileImageProps>;
};

export const getPinWebsiteLink = (
    pin: Pick<DetailedPinWithChecked, 'Target'>
) => {
    switch (pin.Target.__typename) {
        case 'Project':
        case 'Product':
        case 'Article':
            return pin.Target.Professional?.Website || '';
        case 'Professional':
            return pin.Target.Website || '';
        case 'MemberUpload':
        case 'LibraryImage':
        default:
            return '';
    }
};

export const getPinProfessional = (
    pin: Pick<DetailedPinWithChecked, 'Target'>
) => {
    switch (pin.Target.__typename) {
        case 'Project':
        case 'Product':
        case 'Article':
            return pin.Target.Professional;
        case 'Professional':
            return pin.Target;
        case 'MemberUpload':
        case 'LibraryImage':
        default:
            return undefined;
    }
};
